import type { SiteId } from "~/hec/utils/site-id";

const AUTH_PATHS = new Set(["/login", "/register", "/reset-password"]);

function isAuthPath(currentPath: string): boolean {
  return AUTH_PATHS.has(new URL(currentPath, "http://x").pathname);
}

/**
 * The `redirectTo` value to plumb through a link, given the current location.
 * Empty when the caller is already on an auth screen, so we don't loop back to
 * /login.
 *
 * `currentPath` is expected to be `location.pathname + location.search`.
 *
 * CONSUMED CLIENT-SIDE. Since SFCC-11869 the /login and /register actions return
 * success data rather than a redirect, so this value never reaches a server
 * handler (and no longer passes through `safeRedirect`): AccountDrawer navigates
 * to it itself once the fetcher settles. An empty value therefore means "do not
 * navigate" — the shopper stays on the page the drawer opened over, where the
 * old server fallback sent them to `/my-account`.
 */
export function redirectToParam(currentPath: string): string {
  return isAuthPath(currentPath) ? "" : currentPath;
}

/**
 * Is this user-supplied value a safe same-origin path? Rejects `//host`, and
 * backslashes/control chars because browsers fold those into `//` when
 * resolving. Shared by every consumer of an attacker-supplied `redirectTo`:
 * `safeRedirect` (server, `Location:` header) and AccountDrawer (client, where
 * RR7 falls back to `location.assign` on a cross-origin `pushState`).
 */
export function isSameOriginPath(to: string): boolean {
  return to.startsWith("/") && !/^\/\/|[\\\t\n\r]/.test(to);
}

/**
 * Build a /register href that carries the current location as a `redirectTo`
 * query param, so the shopper returns to the page they came from. The /register
 * loader converts the param into a `#register?redirectTo=` hash, which opens
 * AccountDrawer with that destination (see `redirectToParam` on who consumes it).
 * When the caller is already on an auth screen, returns the bare path, and no
 * post-registration navigation happens.
 *
 * `currentPath` is expected to be `location.pathname + location.search`.
 */
export function withRedirectTo(
  authPath: "/register",
  currentPath: string,
): string {
  const target = redirectToParam(currentPath);
  return target
    ? `${authPath}?redirectTo=${encodeURIComponent(target)}`
    : authPath;
}

/**
 * Per-site Google OAuth provider ID, mirroring the SFRA `oauthProviderID` site
 * custom preference (read by `org_hec/.../account/components/oauth.isml`). Keep
 * in lockstep with the SFRA site prefs.
 */
const OAUTH_PROVIDER_ID_BY_SITE: Record<SiteId, string> = {
  FSASTORE: "GoogleFSA",
  HSASTORE: "GoogleHSA",
  WDH: "GoogleWDH",
};

/**
 * Where SFRA sends the shopper once Google hands them back; the numbers index
 * `org_hec/cartridge/config/oAuthRenentryRedirectEndpoints.js on origin/develop`.
 * 6 is not a route but `session.privacy.oauthLoginRefererUrl` — "back where you
 * came from" — set from `?destination` / Referer at
 * `tpa/cartridges/plugin_tpa/cartridge/controllers/Login.js:31-32 on origin/develop`.
 */
const OAUTH_REENTRY = { account: 1, checkout: 2, referrer: 6 } as const;

export type OAuthReentryTarget = keyof typeof OAUTH_REENTRY;

/**
 * Build the SFRA `Login-OAuthLogin` URL for Google sign-in. In the hybrid model
 * the Workers storefront hands OAuth off to SFRA via a full-document
 * navigation: SFRA runs the Google round-trip and bridges the SLAS session back
 * via Set-Cookie. The path is SFRA-proxied (`^/on/demandware` in legacy-routes)
 * so it stays relative to the current host (dev / preview / prod).
 *
 * Pick `target` to match the SFRA surface being replaced — its own page decides the
 * number. `default` is the locale segment (all three sites are en-US); `siteId`
 * doubles as the SFRA Site ID (`Sites-…-Site`).
 */
export function buildGoogleOAuthUrl(
  siteId: SiteId,
  target: OAuthReentryTarget = "referrer",
): string {
  const provider = OAUTH_PROVIDER_ID_BY_SITE[siteId];
  return `/on/demandware.store/Sites-${siteId}-Site/default/Login-OAuthLogin?oauthProvider=${provider}&oauthLoginTargetEndPoint=${OAUTH_REENTRY[target]}`;
}
